import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { href: "#services", label: "Services" },
  { href: "#process", label: "Process" },
  { href: "#automations", label: "Automations" },
  { href: "#about", label: "About" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 28);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ease-out ${
        scrolled
          ? "bg-[#07070A]/85 backdrop-blur-2xl border-b border-white/[0.05] shadow-[0_1px_0_0_rgba(255,255,255,0.03)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-[68px] flex items-center justify-between">

        {/* Logo */}
        <a href="#" data-testid="navbar-logo" className="flex items-center gap-2.5 group flex-shrink-0">
          <div className="relative w-8 h-8 transition-transform duration-300 group-hover:scale-105">
            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 blur-lg opacity-0 group-hover:opacity-50 transition-opacity scale-125" />
            <img src="/brand/aiflow-icon.svg" alt="AIFlow Labs" className="relative w-8 h-8 rounded-xl" />
          </div>
          <span className="font-semibold text-[16px] tracking-tight text-white">
            AIFlow <span className="text-white/35 font-normal">Labs</span>
          </span>
        </a>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-7">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-[13.5px] text-white/45 hover:text-white/90 transition-colors duration-200 font-medium tracking-[-0.01em]"
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://wa.me/918248195127?text=Hi%20AIFlow%20Labs!%20I%20want%20to%20book%20a%20free%20strategy%20call."
            target="_blank"
            rel="noopener noreferrer"
            className="text-[13px] text-white/55 hover:text-white/90 transition-colors font-medium"
          >
            WhatsApp
          </a>
          <a
            href="#cta"
            data-testid="navbar-cta"
            className="btn-shine relative px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-[13px] font-semibold transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25 tracking-[-0.01em]"
          >
            Book a Call
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="navbar-mobile-menu"
          className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg border border-white/[0.07] bg-white/[0.03] text-white/60 hover:text-white hover:bg-white/[0.06] transition-all"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="md:hidden bg-[#0A0A0D]/97 backdrop-blur-2xl border-b border-white/[0.05] overflow-hidden"
          >
            <div className="px-6 py-5 flex flex-col gap-1">
              {links.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-[14px] text-white/55 hover:text-white font-medium py-2.5 transition-colors"
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <div className="h-px bg-white/[0.05] my-2" />
              <a
                href="#cta"
                data-testid="navbar-mobile-cta"
                className="mt-1 px-5 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-[13.5px] font-semibold text-center transition-colors"
                onClick={() => setMenuOpen(false)}
              >
                Book a Call
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
